import { useEffect, useRef, useState } from 'react';
import type { RTCIceServerLike, StreamSource } from '@rabbitfuel/shared';
import type { PeerState } from './webrtc.ts';

/**
 * Das Bild eines RTMP-Eingangs abholen -- per WHEP beim eigenen Server.
 *
 * Ein Eingang hat keinen Publisher im Raum, der Angebote schicken koennte.
 * Stattdessen schickt der Empfaenger sein Angebot per HTTP, und der
 * Empfangsdienst antwortet (WHEP). Danach ist es eine gewoehnliche
 * PeerConnection, und der Rest der Seite merkt keinen Unterschied: es gibt
 * einen MediaStream je Quelle, wie bei einem Handy.
 */

/** Laenger wartet das Angebot nicht auf eigene Kandidaten -- es geht mit denen, die da sind. */
const GATHER_MS = 1500;
/** Neuversuche mit wachsendem Abstand, bis hierhin. */
const MAX_RETRY_MS = 15_000;

class WhepPlayer {
  /** Bleibt ueber Neuaufbauten derselbe -- so muss kein Video neu verdrahtet werden. */
  readonly stream = new MediaStream();
  private pc: RTCPeerConnection | null = null;
  private session: string | null = null;
  private retry: number | undefined;
  private attempts = 0;
  private closed = false;

  constructor(
    private url: string,
    private iceServers: RTCIceServerLike[],
    private onState: (state: PeerState) => void,
  ) {}

  async connect() {
    window.clearTimeout(this.retry);
    this.teardown();
    if (this.closed) return;
    const pc = new RTCPeerConnection({ iceServers: this.iceServers as RTCIceServer[], bundlePolicy: 'max-bundle' });
    this.pc = pc;
    pc.addTransceiver('video', { direction: 'recvonly' });
    pc.addTransceiver('audio', { direction: 'recvonly' });
    pc.ontrack = (event) => {
      // Neue Spur derselben Art ersetzt die alte -- das Video laeuft einfach weiter.
      for (const old of this.stream.getTracks()) {
        if (old.kind === event.track.kind) this.stream.removeTrack(old);
      }
      this.stream.addTrack(event.track);
    };
    pc.onconnectionstatechange = () => {
      if (pc !== this.pc) return;
      this.onState(pc.connectionState);
      if (pc.connectionState === 'connected') this.attempts = 0;
      if (pc.connectionState === 'failed') this.later();
    };
    this.onState('connecting');
    try {
      await pc.setLocalDescription(await pc.createOffer());
      await gathered(pc);
      const res = await fetch(this.url, {
        method: 'POST',
        headers: { 'content-type': 'application/sdp' },
        body: pc.localDescription?.sdp ?? '',
      });
      if (res.status !== 201) throw new Error(`WHEP ${res.status}`);
      const answer = await res.text();
      if (pc !== this.pc) return;
      this.session = res.headers.get('location');
      await pc.setRemoteDescription({ type: 'answer', sdp: answer });
    } catch {
      if (pc === this.pc) this.later();
    }
  }

  /** Noch einmal, mit wachsendem Abstand -- das Signal kann gerade erst kommen. */
  later() {
    if (this.closed) return;
    this.onState('failed');
    window.clearTimeout(this.retry);
    const delay = Math.min(MAX_RETRY_MS, 1000 * 2 ** this.attempts++);
    this.retry = window.setTimeout(() => void this.connect(), delay);
  }

  private teardown() {
    const session = this.session;
    this.session = null;
    // Dem Server Bescheid geben, damit die Sitzung nicht bis zum Zeitlimit haengt.
    if (session) void fetch(session, { method: 'DELETE', keepalive: true }).catch(() => undefined);
    this.pc?.close();
    this.pc = null;
  }

  close() {
    this.closed = true;
    window.clearTimeout(this.retry);
    this.teardown();
    for (const track of this.stream.getTracks()) track.stop();
  }
}

/** Wartet, bis die eigenen Kandidaten gesammelt sind -- WHEP schickt sie mit dem Angebot. */
function gathered(pc: RTCPeerConnection): Promise<void> {
  if (pc.iceGatheringState === 'complete') return Promise.resolve();
  return new Promise((resolve) => {
    const timer = window.setTimeout(resolve, GATHER_MS);
    pc.addEventListener('icegatheringstatechange', () => {
      if (pc.iceGatheringState !== 'complete') return;
      window.clearTimeout(timer);
      resolve();
    });
  });
}

/**
 * Die Streams der gewuenschten RTMP-Quellen. Steht eine Quelle auf
 * „verbindet neu" (Signal weg), bleibt das letzte Bild stehen und es wird
 * nicht nachgefragt; kommt sie zurueck, baut der Player sofort neu auf.
 */
export function useIngestStreams(
  sources: StreamSource[],
  slug: string | undefined,
  iceServers: RTCIceServerLike[],
): { streams: Record<string, MediaStream>; states: Record<string, PeerState> } {
  const players = useRef(new Map<string, WhepPlayer>());
  /** War die Quelle beim letzten Durchlauf live? Neu aufgebaut wird nur beim Wechsel auf live. */
  const wasLive = useRef(new Map<string, boolean>());
  const [streams, setStreams] = useState<Record<string, MediaStream>>({});
  const [states, setStates] = useState<Record<string, PeerState>>({});
  const iceKey = JSON.stringify(iceServers);
  const key = sources
    .map((s) => `${s.id}:${s.ingest?.inputId}:${s.live && !s.reconnecting ? 'on' : 'off'}`)
    .sort()
    .join('|');

  useEffect(() => {
    if (!slug) return;
    const want = new Map(sources.map((s) => [s.id, s]));
    for (const [id, player] of players.current) {
      if (want.has(id)) continue;
      player.close();
      players.current.delete(id);
      wasLive.current.delete(id);
      setStreams(({ [id]: _gone, ...rest }) => rest);
      setStates(({ [id]: _gone, ...rest }) => rest);
    }
    for (const [id, source] of want) {
      const live = source.live && !source.reconnecting;
      const before = wasLive.current.get(id);
      wasLive.current.set(id, live);
      const existing = players.current.get(id);
      if (existing) {
        // Signal ist zurueck -- nicht auf den naechsten Neuversuch warten.
        if (live && before === false) void existing.connect();
        continue;
      }
      const player = new WhepPlayer(
        `/api/events/${slug}/ingest/${source.ingest!.inputId}/whep`,
        iceServers,
        (state) => setStates((prev) => (prev[id] === state ? prev : { ...prev, [id]: state })),
      );
      players.current.set(id, player);
      setStreams((prev) => ({ ...prev, [id]: player.stream }));
      if (live) void player.connect();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, slug, iceKey]);

  // Beim Verlassen der Seite alles schliessen.
  useEffect(
    () => () => {
      for (const player of players.current.values()) player.close();
      players.current.clear();
      wasLive.current.clear();
    },
    [],
  );

  return { streams, states };
}
